import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Building, Floor, Town } from 'src/api/types';
import { PlaceType } from 'src/types';
import { getDateTime } from 'src/utils/getDateTime';

import { getIsMobileWithTouchscreen } from '../agent/selectors';
import { fetchLastBookings } from '../lastBookings/services';
import { startAppListening } from '../listenerMiddleware';
import { sidesheetActions } from '../sidesheet/slice';

import { defaultDeskBookingFilters, initialState } from './initialState';
import {
  desksExcludedDatesPollingStarted,
  desksExcludedDatesPollingStopped,
  desksPollingStarted,
  desksPollingStopped,
  fetchDesks,
  fetchExcludedDates,
} from './services';

const getCurrentDate = (town: Town | undefined): string | undefined => {
  if (town?.timeZone === undefined) return undefined;

  const date = getDateTime({
    timezoneOffset: town.timeZone,
  });
  return date.toISOString();
};

export const desksSlice = createSlice({
  name: 'desks',
  initialState,
  reducers: {
    setTown: (state, action: PayloadAction<Town | undefined>) => {
      state.town = action.payload;

      const date = getCurrentDate(action.payload);
      state.date = date;
    },
    setBuilding: (state, action: PayloadAction<Building | undefined>) => {
      state.building = action.payload;
    },
    setFloor: (state, action: PayloadAction<Floor | undefined>) => {
      state.floor = action.payload;
    },
    clearLocation: state => {
      state.town = undefined;
      state.building = undefined;
      state.floor = undefined;
    },
    setDate: (state, action: PayloadAction<string | undefined>) => {
      state.date = action.payload;
    },
    setByWindow: (state, action: PayloadAction<boolean>) => {
      state.byWindow = action.payload;
    },
    setWithMonitor: (state, action: PayloadAction<boolean>) => {
      state.withMonitor = action.payload;
    },
    setWithCharger: (state, action: PayloadAction<boolean>) => {
      state.withCharger = action.payload;
    },
    setWithDivider: (state, action: PayloadAction<boolean>) => {
      state.withDivider = action.payload;
    },
    setByWindowFilter: (state, action: PayloadAction<boolean>) => {
      state.filters = {
        ...state.filters,
        byWindow: action.payload,
      };
    },
    setWithMonitorFilter: (state, action: PayloadAction<boolean>) => {
      state.filters = {
        ...state.filters,
        withMonitor: action.payload,
      };
    },
    setWithChargerFilter: (state, action: PayloadAction<boolean>) => {
      state.filters = {
        ...state.filters,
        withCharger: action.payload,
      };
    },
    setWithDividerFilter: (state, action: PayloadAction<boolean>) => {
      state.filters = {
        ...state.filters,
        withDivider: action.payload,
      };
    },
    applyFilters: state => {
      state.filters = {
        byWindow: state.byWindow,
        withMonitor: state.withMonitor,
        withCharger: state.withCharger,
        withDivider: state.withDivider,
      };
    },
    resetFilters: state => {
      state.filters = defaultDeskBookingFilters;
      state.withCharger = defaultDeskBookingFilters.withCharger;
      state.byWindow = defaultDeskBookingFilters.byWindow;
      state.withMonitor = defaultDeskBookingFilters.withMonitor;
      state.withDivider = defaultDeskBookingFilters.withDivider;
    },
    setDeskId: (state, action: PayloadAction<string | undefined>) => {
      state.deskId = action.payload;
    },
    setExcludedDates: (state, action: PayloadAction<Set<string>>) => {
      state.excludedDates = action.payload;
    },
    setDeskBookingSheetIsOpened: (state, action: PayloadAction<boolean>) => {
      state.deskBookingSheetIsOpened = action.payload;
    },
    openDeskBookingSheet: state => {
      state.deskBookingSheetIsOpened = true;
    },
    closeDeskBookingSheet: state => {
      state.deskBookingSheetIsOpened = false;
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchDesks.pending, (state, action) => {
      state.error = undefined;
      if (action.meta.arg) state.loading = true;
      state.currentRequestId = action.meta.requestId;
    });
    builder.addCase(fetchDesks.rejected, (state, action) => {
      if (state.currentRequestId !== action.meta.requestId) return;

      state.error = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchDesks.fulfilled, (state, action) => {
      if (state.currentRequestId !== action.meta.requestId) return;

      state.loading = false;
      state.bookingObjects = action.payload.bookingObjects;

      if (action.meta.arg) {
        state.backgroundData = action.payload.backgroundData;
        state.staticObjects = action.payload.staticObjects;
      }

      state.currentRequestId = undefined;
    });

    builder.addCase(fetchExcludedDates.fulfilled, (state, action) => {
      state.excludedDates = action.payload;
    });

    builder.addCase(fetchLastBookings.pending, state => {
      state.locationError = undefined;
      state.locationLoading = true;
    });
    builder.addCase(fetchLastBookings.rejected, (state, action) => {
      state.locationError = action.payload;
      state.locationLoading = false;
    });
    builder.addCase(fetchLastBookings.fulfilled, (state, action) => {
      state.locationLoading = false;

      if (state.town && state.building && state.floor) return;

      const deskLocation = action.payload[PlaceType.Desk];

      const town = deskLocation?.town;
      const building = deskLocation?.building;
      const floor = deskLocation?.floor.isParking
        ? undefined
        : deskLocation?.floor;

      state.town = town;
      state.building = building;
      state.floor = floor;

      const date = getCurrentDate(town);
      state.date = date;
    });
  },
});

export const { actions: desksActions, reducer: desksReducer } = desksSlice;

export const openDesk = createAction<string, 'desks/open'>('desks/open');

startAppListening({
  actionCreator: desksActions.setDate,
  effect: async (action, listenerApi) => {
    await listenerApi.dispatch(desksPollingStopped());
    await listenerApi.dispatch(fetchDesks(false));
    await listenerApi.dispatch(desksPollingStarted(false));
  },
});

startAppListening({
  actionCreator: desksPollingStarted,
  effect: async (action, listenerApi) => {
    listenerApi.unsubscribe();

    if (action.payload) await listenerApi.dispatch(fetchDesks(false));

    const refreshTask = listenerApi.fork(async forkApi => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        await forkApi.delay(10000);
        // eslint-disable-next-line no-await-in-loop
        await listenerApi.dispatch(fetchDesks(false));
      }
    });

    await listenerApi.condition(desksPollingStopped.match);
    refreshTask.cancel();

    listenerApi.subscribe();
  },
});

startAppListening({
  actionCreator: desksExcludedDatesPollingStarted,
  effect: async (action, listenerApi) => {
    listenerApi.unsubscribe();

    if (action.payload) await listenerApi.dispatch(fetchExcludedDates());

    const refreshTask = listenerApi.fork(async forkApi => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        await forkApi.delay(10000);
        // eslint-disable-next-line no-await-in-loop
        await listenerApi.dispatch(fetchExcludedDates());
      }
    });

    await listenerApi.condition(desksExcludedDatesPollingStopped.match);
    refreshTask.cancel();

    listenerApi.subscribe();
  },
});

startAppListening({
  actionCreator: openDesk,
  effect: (action, listenerApi) => {
    const state = listenerApi.getState();

    const isMobile = getIsMobileWithTouchscreen(state);

    listenerApi.dispatch(desksActions.setDeskId(action.payload));
    if (isMobile) {
      listenerApi.dispatch(desksActions.openDeskBookingSheet());
    } else {
      listenerApi.dispatch(sidesheetActions.open('deskBooking'));
    }
  },
});

startAppListening({
  predicate: (action, current, previous) => {
    return (
      current.sidesheet.current !== 'deskBooking' &&
      previous.sidesheet.current === 'deskBooking'
    );
  },
  effect: (action, listenerApi) => {
    listenerApi.dispatch(desksActions.setDeskId(undefined));
  },
});
